/**
 * Timezone helpers — mirror Flutter `update_timezone.dart:15-44`.
 *
 * Flutter uses `FlutterTimezone.getLocalTimezone()` then normalizes
 * `Asia/Calcutta` → `Asia/Kolkata` (the deprecated IANA name still
 * comes back on some platforms). React mirrors via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` + same normalization.
 *
 * Magic strings copied verbatim from `scrips_core/lib/constants/app_constants.dart`:
 *   - `Web` (capital W) — Flutter `deviceWeb` constant (line 653)
 *   - `web` (lowercase) — Flutter `kIsWeb` shortcut for deviceId (login_data_source_impl.dart:331)
 */

/** Flutter `deviceWeb` constant (capital W) — used as `deviceType` body field. */
export const DEVICE_TYPE_WEB = 'Web';

/** Flutter `kIsWeb` deviceId literal (lowercase) — used as `deviceId` body field. */
export const DEVICE_ID_WEB = 'web';

/**
 * Normalize a raw IANA zone the way Flutter does — `Asia/Calcutta` (the
 * deprecated name some platforms still emit) → `Asia/Kolkata`; everything
 * else verbatim. MUST be applied to BOTH sides of the login timezone-mismatch
 * comparison (the device tz AND the JWT `zoneinfo` claim). Applying it to only
 * one side made an India user's `Asia/Calcutta` JWT claim disagree with the
 * normalized device tz → a spurious mismatch popup every login (Flutter maps
 * the `zoneinfo` side too, `login_data_source_impl.dart`).
 */
export function normalizeIana(tz: string | null | undefined): string | undefined {
  if (!tz) return tz ?? undefined;
  return tz === 'Asia/Calcutta' ? 'Asia/Kolkata' : tz;
}

/**
 * Get the browser's local IANA timezone with Flutter's Calcutta-Kolkata
 * normalization applied.
 *
 *   "Asia/Calcutta" → "Asia/Kolkata"
 *   everything else → returned verbatim
 *
 * Falls back to "UTC" if the Intl API is unavailable (should never happen
 * in evergreen browsers; defensive only).
 */
export function getLocalTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return normalizeIana(tz) || 'UTC';
  } catch {
    return 'UTC';
  }
}
